import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import BearContext, { BearContextValue } from "contexts/BearContext";
import useFetchMetadata from "hooks/useFetchMetadata";
import BearRedirect from "components/BearRedirect";
import useTokenIdParam from "hooks/useTokenIdParam";
import useBearVestingStats from "hooks/useBearVestingStats";
import useHoneyConsumed from "hooks/useHoneyConsumed";
import { usePrevious } from "@chakra-ui/react";
import useBearLevelDetails from "hooks/useBearLevelDetails";

type BearProviderProps = {};

const BearProvider: FunctionComponent<BearProviderProps> = ({ children }) => {
  const tokenId = useTokenIdParam();
  const lastTokenId = usePrevious(tokenId);

  const [metadata, fetchMeta, isLoading] = useFetchMetadata(tokenId);
  const [vestingStats, readStats] = useBearVestingStats(tokenId);
  const [honeyConsumed, readConsumed] = useHoneyConsumed(tokenId);
  const levelDetails = useBearLevelDetails(honeyConsumed);

  const fetchMetadata = useCallback(() => {
    if (tokenId) {
      fetchMeta(tokenId);
    }
  }, [tokenId, fetchMeta]);

  const readVestingStats = useCallback(() => {
    if (tokenId) {
      readStats();
    }
  }, [tokenId, readStats]);

  const readHoneyConsumed = useCallback(() => {
    if (tokenId) {
      readConsumed();
    }
  }, [tokenId, readConsumed]);

  useEffect(() => {
    BearRedirect.storeLastTokenId(tokenId);

    if (lastTokenId === undefined) {
      return;
    }

    if (tokenId !== lastTokenId) {
      fetchMetadata();
      readVestingStats();
      readHoneyConsumed();
    }
  }, [
    tokenId,
    lastTokenId,
    fetchMetadata,
    readVestingStats,
    readHoneyConsumed,
  ]);

  const contextValue = useMemo<BearContextValue>(
    () => ({
      tokenId,
      furColor: metadata?.traits[1],
      level: levelDetails?.currentLevel,
      levelDetails,
      metadata,
      vestingStats,
      honeyConsumed,
      isLoading,
      fetchMetadata,
      readVestingStats,
      readHoneyConsumed,
    }),
    [
      tokenId,
      metadata,
      vestingStats,
      honeyConsumed,
      levelDetails,
      isLoading,
      fetchMetadata,
      readVestingStats,
      readHoneyConsumed,
    ]
  );

  return (
    <BearContext.Provider value={contextValue}>{children}</BearContext.Provider>
  );
};

export default BearProvider;
